import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api, { getClients } from '../services/api';

const PROVIDERS = ['Sadad','MyFatoorah','UPayments','Tap','Hesabe'];
const TYPES = ['Single','Multi'];
const EMPTY = { client_id:'', provider:'Sadad', gateway_type:'Single', email:'', password:'', notes:'' };

export default function PaymentGatewaysPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [docFile, setDocFile]   = useState(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const { data:gwData, isLoading } = useQuery({
    queryKey:['payment-gateways'],
    queryFn:()=>api.get('/payment-gateways').then(r=>r.data),
  });
  const { data:clientData } = useQuery({
    queryKey:['clients-list'],
    queryFn:()=>getClients({limit:200}).then(r=>r.data),
  });

  const readFile = file => new Promise(resolve => {
    const r = new FileReader();
    r.onload = e => resolve(e.target.result);
    r.readAsDataURL(file);
  });

  const createMut = useMutation({
    mutationFn: async data => {
      if (docFile) { data.document_file = await readFile(docFile); data.document_filename = docFile.name; }
      return api.post('/payment-gateways', data);
    },
    onSuccess:()=>{ qc.invalidateQueries(['payment-gateways']); setShowForm(false); setForm(EMPTY); setDocFile(null); toast.success('Gateway added'); },
    onError: e=>toast.error(e.response?.data?.message||'Error'),
  });

  const updateMut = useMutation({
    mutationFn: async ({id,data}) => {
      if (docFile) { data.document_file = await readFile(docFile); data.document_filename = docFile.name; }
      return api.put('/payment-gateways/'+id, data);
    },
    onSuccess:()=>{ qc.invalidateQueries(['payment-gateways']); setEditing(null); setDocFile(null); toast.success('Updated'); },
  });

  const deleteMut = useMutation({
    mutationFn: id=>api.delete('/payment-gateways/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['payment-gateways']); toast.success('Deleted'); },
  });

  const downloadDoc = async (id, name) => {
    try {
      const res = await api.get('/payment-gateways/'+id+'/document', { responseType:'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href=url; a.download=name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('File not found'); }
  };

  const handleSave = () => {
    if (!form.client_id || !form.provider) return toast.error('Client and provider are required');
    editing ? updateMut.mutate({id:editing.id, data:form}) : createMut.mutate(form);
  };

  const openEdit = gw => {
    setEditing(gw);
    setForm({ client_id:gw.client_id, provider:gw.provider, gateway_type:gw.gateway_type, email:gw.email||'', password:gw.password||'', notes:gw.notes||'' });
    setDocFile(null);
    setShowForm(false);
  };

  const gateways = gwData?.data || [];
  const clients  = clientData?.data || [];

  return (
    <div>
      <div className="g3" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Gateways</div><div className="stat-val">{gateways.length}</div></div>
        <div className="card-sm"><div className="stat-label">Single</div><div className="stat-val">{gateways.filter(g=>g.gateway_type==='Single').length}</div></div>
        <div className="card-sm"><div className="stat-label">Multi</div><div className="stat-val" style={{color:'var(--info)'}}>{gateways.filter(g=>g.gateway_type==='Multi').length}</div></div>
      </div>

      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button className="btn btn-primary" onClick={()=>{setShowForm(v=>!v);setEditing(null);setForm(EMPTY);setDocFile(null);}}>+ Add Payment Gateway</button>
      </div>

      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{editing?'Edit Payment Gateway':'New Payment Gateway'}</div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Client *</label>
              <select className="form-select" value={form.client_id} onChange={e=>set('client_id',e.target.value)}>
                <option value="">Select client...</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Payment Provider *</label>
              <select className="form-select" value={form.provider} onChange={e=>set('provider',e.target.value)}>
                {PROVIDERS.map(p=><option key={p}>{p}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Type</label>
              <select className="form-select" value={form.gateway_type} onChange={e=>set('gateway_type',e.target.value)}>
                {TYPES.map(t=><option key={t}>{t}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Payment Gateway Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="gateway@email.com"/></div>
            <div className="form-group"><label className="form-label">Password / Notes Reference</label>
              <input className="form-input" value={form.password} onChange={e=>set('password',e.target.value)} placeholder="Reference text (not a login password)"/></div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Upload Contract</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e=>setDocFile(e.target.files[0])}
                style={{padding:'8px 12px',background:'#fff',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)',width:'100%',fontSize:13}}/>
              {docFile&&<div style={{fontSize:11,color:'var(--green)',marginTop:4}}>Selected: {docFile.name}</div>}
              {editing&&editing.document_name&&!docFile&&<div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Current: {editing.document_name}</div>}
            </div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Notes..."/></div>
          </div>
          <div style={{display:'flex',gap:8,marginTop:4}}>
            <button className="btn btn-primary" onClick={handleSave}>Save</button>
            <button className="btn btn-outline" onClick={()=>{setShowForm(false);setEditing(null);setDocFile(null);}}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead><tr><th>Client</th><th>Provider</th><th>Type</th><th>Email</th><th>Password</th><th>Contract</th><th>Notes</th><th>Actions</th></tr></thead>
            <tbody>
              {gateways.length===0&&<tr><td colSpan={8} className="empty-state">No payment gateways found</td></tr>}
              {gateways.map(gw=>(
                <tr key={gw.id}>
                  <td style={{fontWeight:600}}>{gw.client?.name||'--'}</td>
                  <td><span className="badge badge-info">{gw.provider}</span></td>
                  <td><span className={"badge "+(gw.gateway_type==='Multi'?'badge-purple':'badge-muted')}>{gw.gateway_type}</span></td>
                  <td style={{color:'var(--muted)',fontSize:12}}>{gw.email||'--'}</td>
                  <td style={{fontFamily:'monospace',fontSize:12,color:'var(--text)'}}>{gw.password||'--'}</td>
                  <td>{gw.document_name
                    ?<button className="btn btn-outline btn-sm" style={{color:'var(--green)',borderColor:'var(--green)'}} onClick={()=>downloadDoc(gw.id,gw.document_name)}>Download</button>
                    :<span style={{color:'#ccc',fontSize:12}}>--</span>}
                  </td>
                  <td style={{color:'var(--muted)',fontSize:12,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{gw.notes||'--'}</td>
                  <td><div style={{display:'flex',gap:6}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>openEdit(gw)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete?'))deleteMut.mutate(gw.id);}}>X</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
